import { RefreshCw } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { spApiOauthApi, type SpApiAsinRow } from '@/api/client';
import { AsinIdentity } from '@/components/retail/AsinIdentity';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

export function spAsinsQueryKey(credentialId: string, marketplaceId?: string | null) {
  return ['sp-api-asins', credentialId, marketplaceId ?? null] as const;
}

function formatNum(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString();
}

export function SpAccountAsinsTable({
  credentialId,
  marketplaceId,
  enabled = true,
}: {
  credentialId: string;
  marketplaceId?: string | null;
  enabled?: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: spAsinsQueryKey(credentialId, marketplaceId),
    queryFn: async () => {
      const res = await spApiOauthApi.getAsins(credentialId, {
        ...(marketplaceId ? { marketplace_id: marketplaceId } : {}),
      });
      if (!res.success) throw new Error('Failed to load ASINs');
      return res.data;
    },
    enabled: enabled && Boolean(credentialId),
  });

  const refresh = async () => {
    const res = await spApiOauthApi.getAsins(credentialId, {
      refresh: true,
      ...(marketplaceId ? { marketplace_id: marketplaceId } : {}),
    });
    if (!res.success) throw new Error('Failed to refresh ASINs');
    queryClient.setQueryData(spAsinsQueryKey(credentialId, marketplaceId), res.data);
    return res.data;
  };

  const asins: SpApiAsinRow[] = query.data?.asins ?? [];
  const syncedAt = query.data?.synced_at || query.data?.fetched_at;

  return (
    <section className="space-y-3" aria-label="Account ASINs">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">
            ASINs{query.data ? ` (${query.data.count})` : ''}
          </h3>
          {syncedAt ? (
            <p className="text-xs text-foreground-muted mt-0.5">
              {query.data?.cached ? 'From database' : 'Just synced'} ·{' '}
              {new Date(syncedAt).toLocaleString()}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={query.isFetching}
          onClick={() => {
            void refresh().catch(() => {
              void query.refetch();
            });
          }}
        >
          {query.isFetching ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" aria-hidden />
              Syncing…
            </>
          ) : (
            <>
              <RefreshCw className="w-3.5 h-3.5" aria-hidden />
              Refresh ASINs
            </>
          )}
        </Button>
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="w-8 h-8" />
        </div>
      ) : query.isError ? (
        <div className="rounded-lg border border-error/20 bg-error/5 p-4 text-sm text-error">
          {(query.error as Error).message || 'Could not load ASINs'}
          <div className="mt-3">
            <Button type="button" size="sm" variant="secondary" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        </div>
      ) : asins.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface p-6 text-center text-sm text-foreground-muted">
          No ASINs stored for this account yet. Refresh to pull listings/inventory from Amazon
          (may take 1–5 minutes).
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="min-w-full text-sm">
            <thead className="bg-page text-left text-xs uppercase tracking-wide text-foreground-muted">
              <tr>
                <th className="px-4 py-3 font-medium">ASIN</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 font-medium text-right">Sellable on hand</th>
                <th className="px-4 py-3 font-medium text-right">Open PO units</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {asins.map((row) => (
                <tr key={row.id || row.asin} className="text-foreground">
                  <td className="px-4 py-2.5">
                    <AsinIdentity asin={row.asin} title={row.title} imageUrl={row.imageUrl} />
                  </td>
                  <td className="px-4 py-2.5 text-foreground-muted font-mono text-xs">
                    {row.sku || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatNum(row.sellableOnHandInventoryUnits)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatNum(row.openPurchaseOrderUnits)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
